import { useState } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Upload, CopyX, Loader2, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const eur = (v) => `€ ${Number(v || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (iso) => { const [y, m, d] = String(iso || '').split('-'); return d ? `${d}-${m}-${y}` : iso || '—'; };

export const CsvImportButton = ({ categories, onDone }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [vendor, setVendor] = useState('');
  const [btw, setBtw] = useState(21);
  const [category, setCategory] = useState('material');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  const reset = () => { setFile(null); setPreview(null); setVendor(''); };
  const fd = () => { const f = new FormData(); f.append('file', file); f.append('vendor', vendor); f.append('btw_percentage', String(btw)); return f; };

  const doPreview = async () => {
    if (!file) { toast.error('Kies eerst een CSV-bestand'); return; }
    setBusy('preview');
    try {
      const r = await axiosInstance.post('/expenses/import-csv/preview', fd(), { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bestand lezen mislukt', { duration: 8000 });
    } finally { setBusy(''); }
  };

  const doImport = async () => {
    setBusy('import');
    try {
      const f = fd(); f.append('category', category); f.append('skip_duplicates', 'true');
      const r = await axiosInstance.post('/expenses/import-csv', f, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      toast.success(`${r.data.created} uitgaven geïmporteerd · ${r.data.skipped_duplicates} dubbel overgeslagen · ${r.data.invalid} ongeldig`, { duration: 8000 });
      setOpen(false); reset(); onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Importeren mislukt', { duration: 8000 });
    } finally { setBusy(''); }
  };

  const c = preview?.counts;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-gray-300" data-testid="csv-import-btn"><Upload className="w-4 h-4 mr-2" /> CSV importeren</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1a5276]">Uitgaven importeren uit CSV</DialogTitle>
            <DialogDescription>Exporteer je facturen bij de leverancier (bijv. Bouwmaat, Hornbach, Gamma) als CSV en upload ze hier. Bedragen incl. btw; dubbele regels worden automatisch overgeslagen.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><Label className="text-xs">CSV-bestand</Label><Input type="file" accept=".csv,.txt,text/csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="csv-file-input" /></div>
            <div><Label className="text-xs">Leverancier (als niet in bestand)</Label><Input value={vendor} onChange={(e) => { setVendor(e.target.value); setPreview(null); }} placeholder="Bijv. Bouwmaat" data-testid="csv-vendor" /></div>
            <div><Label className="text-xs">Btw</Label>
              <select value={btw} onChange={(e) => { setBtw(Number(e.target.value)); setPreview(null); }} className="block w-full border rounded px-2 py-2 text-sm" data-testid="csv-btw"><option value={21}>21%</option><option value={9}>9%</option><option value={0}>0%</option></select></div>
            <div><Label className="text-xs">Categorie (tenzij geleerde regel)</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="block w-full border rounded px-2 py-2 text-sm" data-testid="csv-category">{Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div className="md:col-span-3 flex items-end"><Button onClick={doPreview} disabled={!!busy || !file} variant="outline" data-testid="csv-preview-btn">{busy === 'preview' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Controleren (voorbeeld)</Button></div>
          </div>
          {preview && (
            <div className="space-y-2" data-testid="csv-preview">
              <p className="text-xs text-gray-500">Herkende kolommen: {Object.entries(preview.mapping).map(([k, v]) => `${k} = "${v}"`).join(' · ')}</p>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="bg-emerald-50 rounded p-2"><p className="text-xs text-gray-500">Nieuw</p><p className="font-bold text-emerald-800" data-testid="csv-count-new">{c.new}</p><p className="text-xs text-gray-500">{eur(preview.sum_new)} incl. btw</p></div>
                <div className="bg-amber-50 rounded p-2"><p className="text-xs text-gray-500">Dubbel (al aanwezig)</p><p className="font-bold text-amber-800" data-testid="csv-count-dup">{c.duplicate}</p><p className="text-xs text-gray-500">worden overgeslagen</p></div>
                <div className="bg-red-50 rounded p-2"><p className="text-xs text-gray-500">Ongeldig</p><p className="font-bold text-red-800" data-testid="csv-count-invalid">{c.invalid}</p><p className="text-xs text-gray-500">datum/bedrag niet leesbaar</p></div>
              </div>
              <div className="max-h-64 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0"><tr><th className="text-left p-1.5">Rij</th><th className="text-left p-1.5">Datum</th><th className="text-left p-1.5">Nummer</th><th className="text-left p-1.5">Leverancier</th><th className="text-left p-1.5">Type</th><th className="text-right p-1.5">Incl. btw</th><th className="text-left p-1.5">Status</th></tr></thead>
                  <tbody className="divide-y">
                    {preview.items.map((it) => (
                      <tr key={it.row} className={it.status === 'duplicate' ? 'bg-amber-50' : it.status === 'invalid' ? 'bg-red-50' : ''}>
                        <td className="p-1.5 text-gray-400">{it.row}</td><td className="p-1.5">{fmtD(it.date)}</td><td className="p-1.5 font-mono">{it.number}</td><td className="p-1.5">{it.vendor}</td><td className="p-1.5">{it.type}</td>
                        <td className={`p-1.5 text-right ${it.amount < 0 ? 'text-red-600' : ''}`}>{it.amount != null ? eur(it.amount) : '—'}</td>
                        <td className="p-1.5">{it.status === 'new' ? <span className="text-emerald-700">nieuw</span> : it.status === 'duplicate' ? <span className="text-amber-700">dubbel{it.note ? ` (${it.note})` : ''}</span> : <span className="text-red-700">{it.problem}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={doImport} disabled={!!busy || !c.new} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="csv-import-confirm-btn">{busy === 'import' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} {c.new} nieuwe uitgaven importeren</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DuplicatesButton = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await axiosInstance.get('/expenses/duplicates'); setGroups(r.data.groups); }
    catch (e) { toast.error(e.response?.data?.detail || 'Controle mislukt'); }
    finally { setBusy(false); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Uitgave "${item.description}" van ${fmtD(item.date)} (${eur(item.amount)}) definitief verwijderen?`)) return;
    try { await axiosInstance.delete(`/expenses/${item.id}`); toast.success('Dubbele bon verwijderd'); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Verwijderen mislukt'); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); load(); }} className="border-gray-300" data-testid="duplicates-btn"><CopyX className="w-4 h-4 mr-2" /> Dubbele bonnen</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1a5276]">Controle op dubbele bonnen</DialogTitle>
            <DialogDescription>Uitgaven met dezelfde leverancier, datum en bedrag — of hetzelfde factuurnummer. Bij import (mail, CSV, bon-scan) worden dubbelen al automatisch overgeslagen; hier zie je wat er eventueel al dubbel in staat.</DialogDescription>
          </DialogHeader>
          {busy && <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Controleren...</p>}
          {groups && !busy && groups.length === 0 && <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="duplicates-none"><CheckCircle2 className="w-4 h-4" /> Geen dubbele bonnen gevonden.</p>}
          {groups && groups.length > 0 && (
            <div className="space-y-3" data-testid="duplicates-list">
              <p className="text-sm text-amber-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {groups.length} groep(en) gevonden. Bewaar er één per groep en verwijder de rest.</p>
              {groups.map((g, gi) => (
                <div key={gi} className="border rounded p-2" data-testid={`duplicate-group-${gi}`}>
                  <p className="text-xs font-semibold text-gray-700">{g.vendor} · {fmtD(g.date)} · {eur(g.amount)} <span className="text-gray-400 font-normal">— {g.reason}</span></p>
                  <ul className="divide-y mt-1">
                    {g.items.map((it, ii) => (
                      <li key={it.id} className="flex items-center gap-3 py-1 text-xs">
                        <span className="flex-1 truncate">{it.description}</span>
                        <span className="text-gray-500">{it.source}</span>
                        <span className={it.receipt_url ? 'text-emerald-700' : 'text-gray-400'}>{it.receipt_url ? 'met bijlage' : 'geen bijlage'}</span>
                        {ii === 0 && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 rounded">oudste — bewaren?</span>}
                        <Button size="sm" variant="ghost" onClick={() => remove(it)} className="text-red-600 hover:bg-red-50 h-7" data-testid={`duplicate-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const STATUS_STYLE = { wachten: 'text-gray-400', bezig: 'text-[#1a5276]', ok: 'text-emerald-700', dubbel: 'text-amber-700', mislukt: 'text-red-700' };
const STATUS_LABEL = { wachten: 'Wachten', bezig: 'AI leest bon...', ok: 'Toegevoegd', dubbel: 'Dubbel — overgeslagen', mislukt: 'Niet gelukt' };

export const BulkScanDialog = ({ state, onClose }) => {
  if (!state) return null;
  const total = state.files.length;
  const ok = state.files.filter((f) => f.status === 'ok').length;
  const failed = state.files.filter((f) => f.status === 'mislukt').length;
  const dup = state.files.filter((f) => f.status === 'dubbel').length;
  const pct = total ? Math.round((state.done / total) * 100) : 0;
  const sumOk = state.files.filter((f) => f.status === 'ok').reduce((a, f) => a + Number(f.result?.amount || 0), 0);
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !state.running) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-scan-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1a5276]">{state.running ? `Bonnen verwerken — ${state.done} van ${total}` : `Klaar — ${ok} van ${total} bonnen toegevoegd`}</DialogTitle>
          <DialogDescription>{state.running ? 'Elke bon wordt door de AI gelezen (leverancier, datum, bedrag, btw, categorie) en direct als uitgave opgeslagen met de bon als bijlage.' : 'Overzicht van alle bestanden. Niet-gelukte bonnen kun je handmatig invoeren via "Nieuwe uitgave" (met de foto als bijlage).'}</DialogDescription>
        </DialogHeader>
        <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-[#3da5a5] transition-all duration-300" style={{ width: `${pct}%` }} /></div>
        <div className="grid grid-cols-4 gap-2 text-center text-sm" data-testid="bulk-scan-counts">
          <div className="bg-gray-50 rounded p-2"><p className="text-xs text-gray-500">Bestanden</p><p className="font-bold" data-testid="bulk-total">{total}</p></div>
          <div className="bg-emerald-50 rounded p-2"><p className="text-xs text-gray-500">Toegevoegd</p><p className="font-bold text-emerald-800" data-testid="bulk-ok">{ok}</p><p className="text-[10px] text-gray-500">{eur(sumOk)}</p></div>
          <div className="bg-amber-50 rounded p-2"><p className="text-xs text-gray-500">Dubbel</p><p className="font-bold text-amber-800" data-testid="bulk-dup">{dup}</p></div>
          <div className="bg-red-50 rounded p-2"><p className="text-xs text-gray-500">Niet gelukt</p><p className="font-bold text-red-800" data-testid="bulk-failed">{failed}</p></div>
        </div>
        <ul className="divide-y border rounded max-h-72 overflow-y-auto text-xs" data-testid="bulk-scan-list">
          {state.files.map((f, i) => (
            <li key={i} className="p-2 flex items-start gap-2" data-testid={`bulk-file-${i}`}>
              {f.status === 'bezig' ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1a5276] mt-0.5" /> : f.status === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5" /> : f.status === 'wachten' ? <span className="w-3.5 h-3.5 inline-block rounded-full border border-gray-300 mt-0.5" /> : <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 ${f.status === 'dubbel' ? 'text-amber-600' : 'text-red-600'}`} />}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{f.name} <span className="text-gray-400 font-normal">({Math.round(f.size / 1024)} kB)</span></p>
                {f.status === 'ok' && f.result && <p className="text-emerald-800">{f.result.vendor} · {fmtD(f.result.date)} · {eur(f.result.amount)} · {f.result.category}</p>}
                {(f.status === 'mislukt' || f.status === 'dubbel') && <p className={STATUS_STYLE[f.status]}>{f.error}</p>}
              </div>
              <span className={`whitespace-nowrap ${STATUS_STYLE[f.status]}`}>{STATUS_LABEL[f.status]}</span>
            </li>
          ))}
        </ul>
        {!state.running && <div className="flex justify-end"><Button onClick={onClose} data-testid="bulk-scan-close">Sluiten</Button></div>}
      </DialogContent>
    </Dialog>
  );
};
